'use client'

import type { LucideIcon } from 'lucide-react'
import { ChevronLeft, ChevronRight, Info } from 'lucide-react'
import { getCreditTypeConfig, type CreditType } from '@/features/subscription/credits/credit-type-utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { format } from 'date-fns'

const STATUS_CONFIG = {
    expired: { label: 'Expired', className: 'bg-[#FEF2F2] text-[#DC2626] border-[#DC2626]/20' },
    depleted: { label: 'Depleted', className: 'bg-[#F5F5F5] text-[#6B7280] border-[#6B7280]/20' },
    scheduled: { label: 'Scheduled', className: 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20' },
} as const

export interface ICreditPool {
    uuid: string
    type: string
    totalCredits: number
    consumedCredits: number
    availableCredits: number
    startsAt: string | Date | null
    expiresAt: string | Date | null
    createdAt: string | Date
    status: string
}

const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return '—'
    return format(new Date(date), 'MMM d, yyyy')
}

interface ICreditPoolHistoryListProps {
    pools: ICreditPool[]
    isLoading: boolean
    emptyIcon: LucideIcon
    emptyMessage: string
    page: number
    totalPages: number
    onPageChange: (page: number) => void
    skeletonCount?: number
}

export const CreditPoolHistoryList = ({
    pools,
    isLoading,
    emptyIcon: EmptyIcon,
    emptyMessage,
    page,
    totalPages,
    onPageChange,
}: ICreditPoolHistoryListProps) => {
    if (isLoading) {
        return <CreditPoolCardSkeleton />
    }

    if (pools.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <EmptyIcon className="w-12 h-12 text-[#D1D5DB] mb-4" />
                <p className="text-[14px] text-[#6B7280]">{emptyMessage}</p>
            </div>
        )
    }

    return (
        <TooltipProvider delayDuration={0}>
            <div>
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-2.5 text-[13px] font-medium text-[#6B7280] border-b border-[#E5E7EB]">
                    <div>Details</div>
                    <div>Date</div>
                    <div className="text-right">Credits</div>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-[#F3F4F6]">
                    {pools.map((pool) => {
                        const typeConfig = getCreditTypeConfig(pool.type as CreditType)
                        const statusConfig =
                            pool.status !== 'active' ? STATUS_CONFIG[pool.status as keyof typeof STATUS_CONFIG] : null

                        return (
                            <div
                                key={pool.uuid}
                                className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-3.5 items-center hover:bg-[#FAFAFA] transition-colors"
                            >
                                {/* Details */}
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[14px] text-[#171717] truncate">{typeConfig.label}</span>
                                    {statusConfig && (
                                        <Badge
                                            variant="outline"
                                            className={`text-[11px] px-2 py-0.5 shrink-0 ${statusConfig.className}`}
                                        >
                                            {statusConfig.label}
                                        </Badge>
                                    )}
                                </div>

                                {/* Date */}
                                <div className="text-[13px] text-[#6B7280]">
                                    {pool.expiresAt ? (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="cursor-help flex items-center gap-1">
                                                    {formatDate(pool.createdAt)}
                                                    <Info className="w-3 h-3 text-[#D1D5DB]" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                                <p>Expires: {formatDate(pool.expiresAt)}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        formatDate(pool.createdAt)
                                    )}
                                </div>

                                {/* Credits */}
                                <div className="text-[14px] font-medium text-right tabular-nums">
                                    <span className="text-[#171717]">{pool.availableCredits.toLocaleString()}</span>
                                    <span className="text-[#6B7280] font-normal">
                                        {' '}
                                        / {pool.totalCredits.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 mt-2">
                        <div className="text-[13px] text-[#6B7280]">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(page - 1)}
                                disabled={page <= 1}
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(page + 1)}
                                disabled={page >= totalPages}
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </TooltipProvider>
    )
}

export const CreditPoolCardSkeleton = () => (
    <div>
        <div className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-2.5 border-b border-[#E5E7EB]">
            <Skeleton className="h-[16px] w-[60px]" />
            <Skeleton className="h-[16px] w-[40px]" />
            <Skeleton className="h-[16px] w-[50px] ml-auto" />
        </div>
        <div className="divide-y divide-[#F3F4F6]">
            {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-3.5 items-center">
                    <Skeleton className="h-[16px] w-[120px]" />
                    <Skeleton className="h-[16px] w-[100px]" />
                    <Skeleton className="h-[16px] w-[60px] ml-auto" />
                </div>
            ))}
        </div>
    </div>
)
